import "styles/banking/bankAccountCards.less";
import { FC, useCallback, useState } from "react";

import { Button } from "antd";
import ellipse from "assets/images/new-questionnaire/ellipse.svg";
import deleteIcon from "assets/images/new-questionnaire/employment/employment-delete.svg";
import { useModalOpener } from "hooks/helpers/useModalOpener";

import DeleteModal from "./Shared/DeleteModal";

interface BankAccountCardsProps {
    accounts: any[];
    horizontalShift?: string;
    handleDeleteBankAccount: (_index: number) => Promise<void>;
}

const BankAccountCards: FC<BankAccountCardsProps> = ({ accounts, horizontalShift, handleDeleteBankAccount }) => {
    const [deletionIndex, setDeletionIndex] = useState<number | null>(null);
    const [showDeleteButton] = useState<boolean>(true);

    const {
        isOpened: isDeletionModalOpened,
        open: handleDeletionModalOpen,
        close: handleDeletionModalClose,
    } = useModalOpener();

    const confirmDeleteClick = useCallback(async () => {
        if (deletionIndex !== null) {
            await handleDeleteBankAccount(deletionIndex);
            setDeletionIndex(null);
            handleDeletionModalClose();
        }
    }, [deletionIndex, handleDeleteBankAccount, handleDeletionModalClose]);

    const handleDeleteButtonClick = useCallback((index: number) => {
        setDeletionIndex(index);
        handleDeletionModalOpen();
    }, [handleDeletionModalOpen]);

    const handleDeletionCancel = useCallback(() => {
        setDeletionIndex(null);
        handleDeletionModalClose();
    }, [handleDeletionModalClose]);

    if (!accounts.length) return null;

    return (
        <div
            style={horizontalShift ? { transform: `translate(${horizontalShift}px, 0px)` } : {}}
        >
            {accounts.map((account, index) => (
                <div className="bank-accounts-cards__account-card-container mb16" key={index}>
                    <div className="bank-accounts-cards__account-card">
                        <div className="bank-accounts-cards__account-info">
                            <div className="bank-accounts-cards__account-info-column">
                                <div className="bank-accounts-cards__account-name">{account.accountName || "Bank Account"}</div>
                                <div className="bank-accounts-cards__account-iban mt8">{account.iban}</div>
                                <div className="bank-accounts-cards__account-description mt8">
                                    <div className="bank-accounts-cards__account-description-text">
                                        {account.region}
                                    </div>
                                    <img
                                        src={ellipse}
                                        height={6}
                                        alt="ellipse"
                                        className="dblock mr6 ml6"
                                    />
                                    <div className="bank-accounts-cards__account-description-text">
                                        {account.ownerName}
                                    </div>
                                </div>
                            </div>
                            <div className="column-right">
                                <img
                                    src={account.logo}
                                    height={62}
                                    alt={account.accountName}
                                />
                                {showDeleteButton && <Button
                                    danger
                                    type="text"
                                    className="horizontal-center red-color mlAuto mt10"
                                    onClick={e => {
                                        e.stopPropagation();
                                        handleDeleteButtonClick(index);
                                    }}
                                    icon={<img height="auto" src={deleteIcon} alt="delete" />}
                                >
                                    Delete
                                </Button>}
                            </div>
                        </div>
                    </div>
                </div>
            ))}
            {isDeletionModalOpened && (
                <DeleteModal
                    isOpen={isDeletionModalOpened}
                    onCancel={handleDeletionCancel}
                    onConfirm={confirmDeleteClick}
                    closeable
                    data={{
                        title: "Deleting this bank account?",
                        text: "Are you sure you want to delete this bank account? This will also delete any answers given already regarding your income sources. This action cannot be undone.",
                        confirmButtonText: "Yes, delete this bank account",
                    }}
                />
            )}
        </div>
    );
};

export default BankAccountCards;
